import { useMemo, useState } from "react";

import { Formik, FormikHelpers } from "formik";
import FormikErrorFocus from "formik-error-focus";

import styled from "styled-components";

import { useLoctool } from "@bigfish/react-loctool";
import { Checkbox, ErrorText, TermsWrapper } from "@components/Checkbox";
import { Input } from "@components/Input";
import { Heading, LeftSide, LeftSideImage, LoginButton, LoginContainer as SplitContainer, LoginForm as Form, RightSide } from "@components/Login";
import { Meta } from "@components/Meta";
import { Typography } from "@components/Typography";
import { bp } from "@styles/theme";
import * as Yup from "yup";
import { GReCaptchaInfo } from "@components/GReCaptchaInfo";
import { Textarea } from "@components/Textarea";
import { Path } from "@utils/Path";
import { useCaptcha } from "@hooks/useReCaptcha";
import { useMutation } from "graphql-hooks";
import { createWithdrawal, createWithdrawalVariables } from "@api/graphql/types";
import { Mutations } from "@api/graphql/queries/Mutations";
import { Container } from "@components/Container";

const isValidHungarianBankAccountNumber = (value: string): boolean => {
    const normalizedValue = value.replace(/\D/g, "");
    return /^\d{16}(\d{8})?$/.test(normalizedValue);
};

const isValidIban = (value: string): boolean => {
    const normalizedValue = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(normalizedValue)) {
        return false;
    }

    const rearrangedValue = normalizedValue.slice(4) + normalizedValue.slice(0, 4);
    let remainder = 0;

    for (const char of rearrangedValue) {
        const codePoint = char.codePointAt(0);
        const numericString = codePoint !== undefined && char >= "A" && char <= "Z" ? String(codePoint - 55) : char;

        for (const digit of numericString) {
            remainder = (remainder * 10 + Number(digit)) % 97;
        }
    }

    return remainder === 1;
};

const WithdrawalPage = () => {
    const loctool = useLoctool();
    const [submitted, setSubmitted] = useState(false);
    const { getCaptchaData } = useCaptcha();
    const [createWithdrawalMutation] = useMutation<createWithdrawal, createWithdrawalVariables>(Mutations.createWithdrawal);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                billingName: Yup.string()
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`)
                    .min(4, loctool.formatMessage({ id: "form.fieldError.billingNameLength" })),
                billingAddress: Yup.string()
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`)
                    .min(4, loctool.formatMessage({ id: "form.fieldError.billingAddressLength" })),
                email: Yup.string()
                    .email(`${loctool.formatMessage({ id: "form.fieldError.email" })}`)
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`),
                phoneNumber: Yup.string()
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`)
                    .matches(/^\+36 \d{2} \d{3} \d{4}$/, loctool.formatMessage({ id: "form.fieldError.phoneNumberFormat" })),
                orderNumber: Yup.string()
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`)
                    .matches(/^MCE\d{9}$/, loctool.formatMessage({ id: "form.fieldError.orderNumberFormat" })),
                magazineName: Yup.string()
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`)
                    .min(4, loctool.formatMessage({ id: "form.fieldError.minLength4" })),
                shippingName: Yup.string()
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`)
                    .min(4, loctool.formatMessage({ id: "form.fieldError.minLength4" })),
                shippingAddress: Yup.string()
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`)
                    .min(4, loctool.formatMessage({ id: "form.fieldError.minLength4" })),
                bankAccountOwner: Yup.string()
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`)
                    .min(4, loctool.formatMessage({ id: "form.fieldError.minLength4" })),
                bankAccountNumber: Yup.string()
                    .required(`${loctool.formatMessage({ id: "form.fieldError.required" })}`)
                    .test("bank-account-number-format", loctool.formatMessage({ id: "form.fieldError.bankAccountNumberFormat" }), value => {
                        if (!value) {
                            return true;
                        }

                        return isValidHungarianBankAccountNumber(value) || isValidIban(value);
                    }),
                note: Yup.string(),
                terms: Yup.boolean().oneOf([true], `${loctool.formatMessage({ id: "form.fieldError.required" })}`),
                confirmation: Yup.boolean().oneOf([true], `${loctool.formatMessage({ id: "form.fieldError.required" })}`),
            }),
        [loctool],
    );

    const onSubmit = async (values: any, { setSubmitting, setStatus }: FormikHelpers<any>) => {
        setStatus({ apiErrorMessage: "" });
        setSubmitting(true);
        try {
            const captcha = await getCaptchaData();
            const result = await createWithdrawalMutation({
                variables: {
                    input: {
                        billingName: values.billingName,
                        billingAddress: values.billingAddress,
                        email: values.email,
                        phoneNumber: values.phoneNumber,
                        orderNumber: values.orderNumber,
                        magazineName: values.magazineName,
                        shippingName: values.shippingName,
                        shippingAddress: values.shippingAddress,
                        bankAccountNumber: values.bankAccountNumber,
                        bankAccountOwner: values.bankAccountOwner,
                        note: values.note,
                    },
                    captcha,
                },
            });
            if (!result.data?.createWithdrawal.success) {
                setStatus({ apiErrorMessage: result.data?.createWithdrawal.message || "Váratlan hiba történt a küldés során." });
                return;
            }
            setSubmitted(true);
            console.log("Form adatok:", values);
        } catch (error) {
            setStatus({ apiErrorMessage: "Váratlan hiba történt a küldés során." });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Meta />
            <SplitContainer className="_ce_measure_row">
                <RightSide className="_ce_measure_column">
                    {submitted ? (
                        <Container>
                            <Heading as="legend">Regisztráltuk elállási nyilatkozatát.</Heading>
                            <Typography.P>
                                Ezúton visszaigazoljuk, hogy kérelmét rendszerünk sikeresen fogadta. Kollégáink az adatokat legkésőbb 72 órán belül
                                feldolgozzák, majd a további teendőkről e-mailben tájékoztatjuk.{" "}
                            </Typography.P>
                            <Typography.P>
                                A megadott e-mail címre rövidesen egy automatikus visszaigazolást is küldünk a benyújtott adatokkal. Amennyiben ezt nem kapja
                                meg, kérjük, vegye fel a kapcsolatot ügyfélszolgálatunkkal, ahol munkatársaink készséggel állnak rendelkezésére.
                            </Typography.P>
                            <Typography.P>
                                Ügyfélszolgálat: <br />
                                Telefon: <Link href="tel:+3615100888">+36 1 510 0888 </Link>
                                <br />
                                E-mail: <Link href="mailto:elofizetes@centralmediacsoport.hu">elofizetes@centralmediacsoport.hu</Link>
                            </Typography.P>
                            <Typography.P>
                                Köszönjük együttműködését! <br />
                                <br />
                                Magazin Centrum
                            </Typography.P>
                        </Container>
                    ) : (
                        <Formik
                            initialValues={{
                                billingName: "",
                                billingAddress: "",
                                email: "",
                                phoneNumber: "",
                                orderNumber: "",
                                magazineName: "",
                                shippingName: "",
                                shippingAddress: "",
                                bankAccountOwner: "",
                                bankAccountNumber: "",
                                note: "",
                                terms: false,
                                confirmation: false,
                            }}
                            validationSchema={validationSchema}
                            onSubmit={onSubmit}
                        >
                            {({ handleChange, handleSubmit, values, errors, touched, isSubmitting, status }) => (
                                <Form onSubmit={handleSubmit} className="_ce_measure_widget">
                                    <Heading as="legend">{loctool.formatMessage({ id: "pages.withdrawal.title" })}</Heading>
                                    <input id="00N3X00000LHbsJ" name="00N3X00000LHbsJ" type="hidden" value="1" />
                                    <Input.Primary
                                        type="text"
                                        name="billingName"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.billingName.label" })}
                                        value={values.billingName}
                                        onChange={handleChange}
                                        error={!!errors.billingName && touched.billingName}
                                        errorMessage={errors.billingName}
                                        maxLength={255}
                                    />
                                    <Input.Primary
                                        type="text"
                                        name="billingAddress"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.billingAddress.label" })}
                                        value={values.billingAddress}
                                        onChange={handleChange}
                                        error={!!errors.billingAddress && touched.billingAddress}
                                        errorMessage={errors.billingAddress}
                                        maxLength={255}
                                    />
                                    <Input.Primary
                                        type="email"
                                        name="email"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.email.label" })}
                                        value={values.email}
                                        onChange={handleChange}
                                        error={!!errors.email && touched.email}
                                        errorMessage={errors.email}
                                        maxLength={255}
                                    />
                                    <Input.Masked
                                        type="text"
                                        inputMode="tel"
                                        name="phoneNumber"
                                        className="login-input"
                                        mask="+36 99 999 9999"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.phoneNumber.label" })}
                                        value={values.phoneNumber}
                                        style={{ backgroundColor: "white" }}
                                        onChange={handleChange}
                                        error={!!errors.phoneNumber && touched.phoneNumber}
                                        errorMessage={errors.phoneNumber}
                                    />
                                    <Input.Primary
                                        type="text"
                                        name="orderNumber"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.orderNumber.label" })}
                                        value={values.orderNumber}
                                        onChange={handleChange}
                                        error={!!errors.orderNumber && touched.orderNumber}
                                        errorMessage={errors.orderNumber}
                                        maxLength={12}
                                    />
                                    <Input.Primary
                                        type="text"
                                        name="magazineName"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.magazineName.label" })}
                                        value={values.magazineName}
                                        onChange={handleChange}
                                        error={!!errors.magazineName && touched.magazineName}
                                        errorMessage={errors.magazineName}
                                        maxLength={255}
                                    />
                                    <Input.Primary
                                        type="text"
                                        name="shippingName"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.shippingName.label" })}
                                        value={values.shippingName}
                                        onChange={handleChange}
                                        error={!!errors.shippingName && touched.shippingName}
                                        errorMessage={errors.shippingName}
                                        maxLength={255}
                                    />
                                    <Input.Primary
                                        type="text"
                                        name="shippingAddress"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.shippingAddress.label" })}
                                        value={values.shippingAddress}
                                        onChange={handleChange}
                                        error={!!errors.shippingAddress && touched.shippingAddress}
                                        errorMessage={errors.shippingAddress}
                                        maxLength={255}
                                    />
                                    <Input.Primary
                                        type="text"
                                        name="bankAccountOwner"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.bankAccountOwner.label" })}
                                        value={values.bankAccountOwner}
                                        onChange={handleChange}
                                        error={!!errors.bankAccountOwner && touched.bankAccountOwner}
                                        errorMessage={errors.bankAccountOwner}
                                        maxLength={255}
                                    />
                                    <Input.Primary
                                        type="text"
                                        name="bankAccountNumber"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.bankAccountNumber.label" })}
                                        value={values.bankAccountNumber}
                                        onChange={handleChange}
                                        error={!!errors.bankAccountNumber && touched.bankAccountNumber}
                                        errorMessage={errors.bankAccountNumber}
                                    />
                                    <Textarea
                                        name="note"
                                        className="login-input"
                                        label={loctool.formatMessage({ id: "pages.withdrawal.form.note.label" })}
                                        value={values.note}
                                        style={{ height: 88 }}
                                        onChange={handleChange}
                                        maxLength={500}
                                    />
                                    <TermsWrapper style={{ display: "grid", gap: "24px" }}>
                                        <div>
                                            <Checkbox
                                                name="terms"
                                                label={loctool.formatHtmlMessage(
                                                    { id: "form.terms" },
                                                    {
                                                        termsLink: (chunk: string) => `<a href=${Path.terms} target='_blank'>${chunk}</a>`,
                                                        privacyLink: (chunk: string) => `<a href=${Path.privacyPolicy} target='_blank'>${chunk}</a>`,
                                                    },
                                                )}
                                                checked={values.terms}
                                                onChange={handleChange}
                                                error={!!errors.terms && touched.terms}
                                                errorMessage={errors.terms}
                                            />
                                        </div>
                                        <div>
                                            <Checkbox
                                                name="confirmation"
                                                label={loctool.formatHtmlMessage({ id: "pages.withdrawal.form.confirmation" })}
                                                checked={values.confirmation}
                                                onChange={handleChange}
                                                error={!!errors.confirmation && touched.confirmation}
                                                errorMessage={errors.confirmation}
                                            />
                                        </div>
                                    </TermsWrapper>
                                    <div style={{ marginBottom: 30 }}>
                                        <GReCaptchaInfo />
                                    </div>
                                    <SubscribeButton onClick={handleSubmit} disabled={isSubmitting}>
                                        {isSubmitting
                                            ? loctool.formatMessage({ id: "pages.withdrawal.form.submitting" })
                                            : loctool.formatMessage({ id: "pages.withdrawal.form.submit" })}
                                    </SubscribeButton>
                                    {status?.apiErrorMessage && <ErrorText>{status.apiErrorMessage}</ErrorText>}
                                    <FormikErrorFocus offset={-200} align={"top"} focusDelay={200} ease={"linear"} duration={250} />
                                </Form>
                            )}
                        </Formik>
                    )}
                </RightSide>
                <LeftSide>
                    <StyledLeftSideImage objectP="center">
                        <img src="/images/withdrawal.jpg" role="presentation" />
                    </StyledLeftSideImage>
                </LeftSide>
            </SplitContainer>
        </>
    );
};

export default WithdrawalPage;

const Link = styled.a`
    font-weight: 600;
    text-decoration: underline;
`;

const SubscribeButton = styled(LoginButton)`
    margin-top: 60px;

    ${bp.small} {
        margin-top: 50px;
    }
`;

const StyledLeftSideImage = styled(LeftSideImage)`
    img {
        display: block;
        position: sticky;
        top: 48px;
        width: 100%;
        max-width: 512px;
        height: auto;
        margin: 48px auto;
    }
`;
